import { buildProfilePictureUrl } from '../profilePictureUrl';
import { isPlaceholderProfilePictureUrl } from '../../../utils/uniqueDefaultAvatar';
import { stripHandlePrefix } from '../../profileIdentity/profileHandleUtils';

/** Display handle with a single leading @ (internal storage omits @). */
export function formatMessagingHandle(handle) {
  const body = stripHandlePrefix(handle);
  return body ? `@${body}` : '';
}

/**
 * Normalize user objects from social API / profile search into messaging UI shape.
 */
export function normalizeMessagingUser(user, baseUrl = process.env.REACT_APP_BASE_URL || '') {
  if (!user) return null;
  const userId = String(user.userId || '').trim();
  if (!userId) return null;

  const displayName =
    user.displayName ||
    user.name ||
    [user.first_name, user.last_name].filter(Boolean).join(' ') ||
    user.handle ||
    user.personal_handle ||
    'User';

  const rawProfilePicture =
    user.profilePictureUrl || user.profile_picture_url || user.social_profile_picture_url || '';
  const identity = { userId, name: displayName };
  const raw = String(rawProfilePicture || '').trim();
  const hasProfilePicture = !isPlaceholderProfilePictureUrl(raw);
  const profilePictureUrl = raw.startsWith('data:')
    ? raw
    : buildProfilePictureUrl(rawProfilePicture, baseUrl, identity);

  const handle = stripHandlePrefix(
    user.handle || user.personal_handle || user.social_handle || ''
  );

  const profileMode =
    String(user.profileMode || '').trim().toLowerCase() === 'social' ? 'social' : 'personal';

  return { userId, displayName, profilePictureUrl, hasProfilePicture, handle, profileMode };
}

export function messagingPeerKey(user) {
  const normalized = normalizeMessagingUser(user);
  if (!normalized?.userId) return '';
  return `${normalized.userId}:${normalized.profileMode}`;
}

export function messagingThreadPath(user) {
  const normalized = normalizeMessagingUser(user);
  if (!normalized?.userId) return '/sm/all-messages';
  const params = new URLSearchParams({ userId: normalized.userId });
  if (normalized.profileMode === 'social') params.set('profileMode', 'social');
  return `/sm/all-messages?${params.toString()}`;
}

export function isSameMessagingPeer(a, b) {
  const left = normalizeMessagingUser(a);
  const right = normalizeMessagingUser(b);
  if (!left || !right) return false;
  return left.userId === right.userId && left.profileMode === right.profileMode;
}

export function conversationMatchesPeer(conversation, user) {
  return isSameMessagingPeer(conversation?.otherUser, user);
}

/** Keep social/personal mode when API payloads omit profileMode (e.g. conversation enrich). */
export function preserveMessagingProfileMode(nextUser, prevUser) {
  const next = normalizeMessagingUser(nextUser);
  if (!next) return normalizeMessagingUser(prevUser);
  const prev = normalizeMessagingUser(prevUser);
  if (prev?.profileMode === 'social' && next.profileMode !== 'social') {
    return {
      ...next,
      profileMode: 'social',
      displayName: prev.displayName || next.displayName,
      handle: prev.handle || next.handle,
      profilePictureUrl: prev.profilePictureUrl || next.profilePictureUrl,
      hasProfilePicture: prev.hasProfilePicture || next.hasProfilePicture,
    };
  }
  return next;
}
